import { API_BASE_URL } from "../../constants/index";
import HttpInterface from "../../helpers/HttpInterface";
import { defaultFetchParam, FetchParam } from "../../Models/FetchParam";
import { buildAPIUrl } from "../../api/apiHelpers";

export class ShipmentsService {
  private httpProxy: HttpInterface;

  /**
   * Creates an instance of ShipmentsService.
   * Initializes an HTTP interface to perform API requests.
   */
  constructor() {
    this.httpProxy = new HttpInterface();
  }

  /**
   * Fetches all shipments from the shipment service API.
   *
   * @param params The query parameters for the API request. Defaults to `defaultFetchParam` if not provided.
   * @returns A promise that resolves with the API response containing the list of shipments.
   */
  all(params: FetchParam = defaultFetchParam): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Builds the complete API URL using the base URL, endpoint, and parameters
    return this.httpProxy.fetch(
      buildAPIUrl(`${API_BASE_URL}/shipment-service/shipments`, params),
      requestOptions,
    );
  }

  /**
   * Fetches a specific shipment by its ID from the API.
   *
   * @param id The unique identifier of the shipment to fetch.
   * @returns A promise that resolves with the API response containing the shipment details.
   */
  get(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific shipment by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/shipment-service/shipments/${id}`,
      requestOptions,
    );
  }

  /**
   * Deletes a specific shipment entry by its ID.
   *
   * @param id - The unique identifier of the record to be deleted.
   * @returns A promise that resolves with the API response for the deletion.
   */
  delete(id: string): Promise<any> {
    const requestOptions = {
      method: "DELETE",
    };

    return this.httpProxy.fetch(
      `${API_BASE_URL}/shipment-service/shipments/${id}`,
      requestOptions,
    );
  }

  /**
   * Deletes a specific shipment entry by its ID.
   *
   * @param id - The unique identifier of the record to be deleted.
   * @returns A promise that resolves with the API response for the deletion.
   */
  confirm(id: string): Promise<any> {
    const requestOptions = {
      method: "GET",
    };

    return this.httpProxy.fetch(
      `${API_BASE_URL}/shipment-service/shipments/${id}/confirm`,
      requestOptions,
    );
  }

  /**
   * Forces the tracking update for a specific shipment.
   * Sends a GET request to the API endpoint to trigger tracking enforcement.
   *
   * @param {string} id - The ID of the shipment to force tracking for.
   * @returns {Promise<any>} - A Promise resolving to the API response.
   */
  forceTracking(id: string): Promise<any> {
    const requestOptions = {
      method: "GET",
    };

    return this.httpProxy.fetch(
      `${API_BASE_URL}/shipment-service/shipments/${id}/force-tracking`,
      requestOptions,
    );
  }

  addTrackingEvent(id: string, payload: object): Promise<any> {
    return this.httpProxy.fetch(
      `${API_BASE_URL}/shipment-service/shipments/${id}/tracking`,
      { method: "POST", body: JSON.stringify(payload) },
    );
  }

  /**
   * Fetches a specific shipment by its ID from the  public API.
   *
   * @param id The unique identifier of the shipment to fetch.
   * @returns A promise that resolves with the API response containing the shipment details.
   */
  getPublic(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific shipment by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/shipment-service/shipments/public/ddt/${id}`,
      requestOptions,
    );
  }

  /**
   * Fetches a specific shipment by its ID from the API.
   *
   * @param id The unique identifier of the shipment to fetch.
   * @returns A promise that resolves with the API response containing the shipment details.
   */
  getRate(id: string): Promise<any> {
    const requestOptions = {
      method: "GET", // HTTP method for the request
    };

    // Constructs the URL for fetching a specific shipment by its ID
    return this.httpProxy.fetch(
      `${API_BASE_URL}/shipment-service/shipments/${id}/rate`,
      requestOptions,
    );
  }
}
